import images from "@assets/images";
import { useDimensionContext } from "@src/DimensionProvider";
import { colors } from "@src/theme";
import { ImageButton02 } from "components/button";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Image, StyleSheet, View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  runOnJS, useAnimatedReaction,
  useAnimatedStyle, useSharedValue, withTiming
} from "react-native-reanimated";
import useOrientationListener from "utils/orientation-helper";
import Items from "./items";

function ActionButton() {
  const isOpen = useSharedValue(0);
  const isDragEnabled = useSharedValue(false);
  const orientation = useOrientationListener();
  const [enableDrag, setEnableDrag] = useState(false);
  const { baseWidth, baseHeight, maxHeight } = useDimensionContext();
  const { screenWidth, screenHeight, circleSize, offX } = useMemo(() => {
    const screenWidth = orientation === "LANDSCAPE" ? baseWidth * 0.1208 : baseWidth * 0.34;
    const screenHeight = screenWidth * 1.25;
  
    return {
      screenWidth: screenWidth, 
      screenHeight: screenHeight,
      circleSize: screenWidth * 0.3,
      offX: (screenWidth / 2) - ((screenWidth * 0.3) / 2),
    };
  },[baseWidth, orientation]);

  const offsetX = useSharedValue(offX);
  const offsetY = useSharedValue(baseHeight * 0.04);
  const translateX = useSharedValue(offX);
  const translateY = useSharedValue(baseHeight * 0.04);
  const panGestureEvent = useMemo(() => {
    return Gesture.Pan()
      .onStart(() => {
      })
      .onUpdate((event) => {
        if (isDragEnabled.value) {
          const newX = offsetX.value + event.translationX;
          const newY = offsetY.value + event.translationY;

          // Clamp to keep within screen
          translateX.value = Math.min(
            Math.max(newX, (screenWidth - circleSize ) / 2),
            baseWidth - (screenWidth + circleSize ) / 2
          );
          translateY.value = Math.min(
            Math.max(newY, 0),
            maxHeight - (screenHeight + circleSize/2)
          );
        }
      })
      .onEnd(() => {
        if (isDragEnabled.value) {
          offsetX.value = translateX.value;
          offsetY.value = translateY.value;
          isDragEnabled.value = false;
        }
      });
  },[isDragEnabled.value, baseWidth, maxHeight, circleSize, screenWidth, screenHeight]);
  
  const longPressGesture = Gesture.LongPress()
    .minDuration(300)
    .onStart(() => {
      isDragEnabled.value = true;
    });
  
  const composedGesture = Gesture.Simultaneous(longPressGesture, panGestureEvent);

  const onAction = useCallback(() => {
    isDragEnabled.value = false;

    if(enableDrag === false){
      isOpen.value = withTiming(isOpen.value === 0 ? 1 : 0);
    }
  }, [isOpen.value, enableDrag]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
    ],
    width: circleSize,
    minHeight: circleSize,
    borderRadius: circleSize / 2,
  }));

  useAnimatedReaction(
    () => isDragEnabled.value,
    (value, previousValue) => {
      if (value !== previousValue) {
        runOnJS(setEnableDrag)(value);
      }
    }
  );

  const onChange = () => {
    // Re-adjust icon position if outside bounds
    // translateX.value = Math.min(translateX.value, baseWidth - (screenWidth + circleSize ) / 2);
    // translateY.value = Math.min(translateY.value, maxHeight - (screenHeight + circleSize/2));
    translateX.value = offX;
    translateY.value = baseHeight * 0.04;

    offsetX.value = translateX.value;
    offsetY.value = translateY.value;
  };

  useEffect(() => {
    onChange();
  }, [baseWidth, maxHeight, circleSize, offX]);

  return (
    <View pointerEvents="box-none" style={[styles.container, 
      {width: baseWidth, height: maxHeight},
      enableDrag && { backgroundColor: colors.translucent}]}>
      <GestureDetector gesture={composedGesture}>
        <>
          <Animated.View
            style={[
              styles.v_circle,
              animatedStyle,
              {
                borderWidth: enableDrag ? 3 : 3,
                shadowColor: enableDrag ? colors.translucent : colors.transparent,
                shadowOffset: enableDrag ? { width: 2, height: 2 } : { width: 0, height: 0 },
                shadowOpacity: 11.6,
                shadowRadius: 10,
              }
            ]}
          >
            <ImageButton02
              onPress={onAction}
              style={[
                styles.btn_float,
                {
                  width: circleSize * 0.8,
                  height: circleSize * 0.8,
                  borderRadius: circleSize * 0.8 / 2,
                },
              ]}
            >
              <Image
                style={{ width: circleSize * 0.7, height: circleSize * 0.7 }}
                source={{uri: images["palm-tree"]}}
                resizeMode="cover"
              />
            </ImageButton02> 
          </Animated.View>
          <Items {...{offsetX, offsetY, translateX, translateY, isOpen}} />
        </>
      </GestureDetector>
    </View>
  );
}

export default ActionButton;

const styles = StyleSheet.create({
  container: {
    width: "100%", height: "100%", 
    position: "absolute", backgroundColor: "transparent"},
  v_circle: {
    zIndex: 1,
    borderWidth: 5,
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    borderColor: colors.yellow,
    backgroundColor: colors.skyblue3,
  },
  btn_float: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 40 / 2,
  },
});
